import { useId, useRef, useState, type KeyboardEvent } from "react";
import { Plus, X } from "lucide-react";
import { useLocale } from "../../../context/LanguageContext";
import { isValidDomain } from "../services/sso-validation";

interface EmailDomainsFieldProps {
  domains: string[];
  onAdd: (domain: string) => void;
  onRemove: (index: number) => void;
  disabled?: boolean;
  errorKey?: string;
}

export function EmailDomainsField({
  domains,
  onAdd,
  onRemove,
  disabled = false,
  errorKey,
}: EmailDomainsFieldProps) {
  const { t } = useLocale();
  const [draft, setDraft] = useState("");
  const [draftError, setDraftError] = useState<string | undefined>();
  const fieldId = useId();
  const errorId = `${fieldId}-error`;
  const helpId = `${fieldId}-help`;
  const liveRef = useRef<HTMLDivElement | null>(null);

  const commit = () => {
    const value = draft.trim().toLowerCase();
    if (!value) return;
    if (!isValidDomain(value)) {
      setDraftError(t("sso.errors.domainInvalid"));
      return;
    }
    if (domains.includes(value)) {
      setDraftError(t("sso.errors.domainDuplicate"));
      return;
    }
    if (domains.length >= 10) {
      setDraftError(t("sso.errors.domainTooMany"));
      return;
    }
    onAdd(value);
    setDraft("");
    setDraftError(undefined);
    if (liveRef.current) {
      liveRef.current.textContent = t("sso.live.domainAdded", { domain: value });
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" || event.key === "," || event.key === " ") {
      event.preventDefault();
      commit();
    } else if (event.key === "Backspace" && draft === "" && domains.length > 0) {
      onRemove(domains.length - 1);
    }
  };

  const fieldDescribedBy = [
    helpId,
    draftError || errorKey ? errorId : undefined,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div>
      <label htmlFor={fieldId} className="block text-sm font-medium text-foreground">
        {t("sso.field.emailDomains.label")}
      </label>
      <p id={helpId} className="mt-1 text-xs text-muted-foreground">
        {t("sso.field.emailDomains.help")}
      </p>

      <div
        className={`mt-2 flex flex-wrap items-center gap-2 rounded-md border bg-background px-2 py-2 transition-colors ${
          disabled
            ? "cursor-not-allowed opacity-60"
            : "border-border focus-within:border-primary focus-within:ring-1 focus-within:ring-primary"
        }`}
      >
        {domains.map((d, index) => (
          <span
            key={d}
            className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-foreground"
          >
            <span>{d}</span>
            <button
              type="button"
              onClick={() => onRemove(index)}
              disabled={disabled}
              aria-label={t("sso.field.emailDomains.remove", { domain: d })}
              className="rounded-full p-0.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed"
            >
              <X size={12} />
            </button>
          </span>
        ))}
        <div className="flex flex-1 items-center gap-1">
          <input
            id={fieldId}
            type="text"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              if (draftError) setDraftError(undefined);
            }}
            onKeyDown={handleKeyDown}
            onBlur={commit}
            disabled={disabled}
            placeholder={t("sso.field.emailDomains.placeholder")}
            aria-invalid={!!(draftError || errorKey)}
            aria-describedby={fieldDescribedBy || undefined}
            className="min-w-[10rem] flex-1 bg-transparent px-1 py-0.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed"
          />
          <button
            type="button"
            onClick={commit}
            disabled={disabled || draft.trim() === ""}
            aria-label={t("sso.field.emailDomains.add")}
            className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Plus size={14} />
          </button>
        </div>
      </div>

      {(draftError || errorKey) && (
        <p id={errorId} className="mt-1 text-xs text-destructive">
          {draftError ?? t(`sso.errors.${errorKey}`)}
        </p>
      )}
      <div ref={liveRef} aria-live="polite" className="sr-only" />
    </div>
  );
}
